/**
 * MeView's styles, kept apart so the screen stays readable (display-code
 * rule: split a file past 500 lines when adding to it).
 */
import styled from '@emotion/styled';

// The page is its own scroller (height, not min-height): the app's MainContent
// has overflow:auto without scrolling itself, which would pin the sticky tab
// bar to a box that scrolls away with the body.
export const Page = styled.div`
  height: 100vh;
  overflow-y: auto;
  box-sizing: border-box;
  background: #0b0d12;
  color: #e8ecf2;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  /* no padding on the scroller: a sticky child sticks to its content edge, so padding here would leave the bar hanging below the top */
`;

export const Column = styled.div`
  width: 100%;
  max-width: calc(42rem + 2.5rem);
  box-sizing: border-box;
  padding: clamp(1.5rem, 6vw, 4rem) 1.25rem 5rem;
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
`;

export const Eyebrow = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #7b8794;
`;

export const Title = styled.h1`
  font-size: clamp(1.7rem, 5vw, 2.4rem);
  line-height: 1.1;
  margin: 0.35rem 0 0;
  font-weight: 700;
  text-wrap: balance;
`;

export const CUBE = 72;

export const Head = styled.div``;

export const HeadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  canvas { flex: 0 0 auto; }
  > div { flex: 1 1 auto; min-width: 0; }
`;

/* The ingress keeps the title's left edge, under the cube's column. */
export const HeadBody = styled.div`
  padding-left: calc(${CUBE}px + 1rem);
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const PHASE_COLOR = {
  upcoming: ['#6fd3c8', 'rgba(111, 211, 200, 0.14)'],
  ongoing: ['#f2c14e', 'rgba(242, 193, 78, 0.16)'],
  past: ['#98a2b3', 'rgba(152, 162, 179, 0.14)'],
  online: ['#8ec1f0', 'rgba(91, 157, 217, 0.16)'],
};

// An online Kuutio carries a second pill beside its phase: ONLINE · TULOSSA.
export const Tags = styled.span`
  display: inline-flex;
  gap: 0.35rem;
  flex: 0 0 auto;
  align-self: center;
`;

// The signed-in person, top right — staff and invited guests alike.
export const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  min-height: 36px;
`;

// The tab's dot: something is still to do in the pre-work.
export const Dot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef5350;
  margin-left: 0.45rem;
  vertical-align: 2px;
`;

export const Tag = styled.span`
  flex: 0 0 auto;
  align-self: center;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.28rem 0.6rem;
  border-radius: 999px;
  color: ${(p) => PHASE_COLOR[p.$phase][0]};
  background: ${(p) => PHASE_COLOR[p.$phase][1]};
`;

export const Transit = styled.div`
  margin-top: 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.95rem;
`;

export const TransitRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  svg { flex: 0 0 auto; margin-top: 0.2rem; color: #7b8794; }
`;

export const Card = styled.div`
  background: #141821;
  border: 1px solid #232a36;
  border-radius: 10px;
  padding: 1.25rem 1.35rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

export const CardButton = styled.button`
  all: unset;
  cursor: pointer;
  background: #141821;
  border: 1px solid #232a36;
  border-radius: 10px;
  padding: 1.1rem 1.35rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;

  &:hover { border-color: #3a4557; }
  &:focus-visible { outline: 2px solid #4cc7ce; outline-offset: 2px; }
`;

export const Row = styled.div`
  display: flex;
  gap: 0.8rem;
  align-items: flex-start;
  font-size: 0.98rem;
  line-height: 1.5;

  svg { flex: 0 0 auto; margin-top: 0.18rem; color: #4cc7ce; }
`;

export const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #7b8794;
  margin: 0;
`;

export const Muted = styled.p`
  color: #97a2b0;
  font-size: 0.94rem;
  line-height: 1.55;
  margin: 0;
`;

export const Dl = styled.dl`
  display: grid;
  grid-template-columns: minmax(7rem, auto) 1fr;
  gap: 0.45rem 1.1rem;
  margin: 0;
  font-size: 0.96rem;

  dt { color: #7b8794; }
  dd { margin: 0; }
`;

/* OpenStreetMap rather than a map library: interactive, no API key, no
   dependency added for one panel. The basemap is light against a dark page —
   a map that looks like a map is worth more here than a matching palette. */
export const MapBox = styled.div`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #1b2029;
`;

// Interactive on purpose: the frame is cross-origin, so nothing outside it
// can follow a pan. A logo centred over it would point at the venue only
// until the first drag, which is why the marker is OSM's own (`marker=` in
// the embed URL) and not ours.
export const MapFrame = styled.iframe`
  width: 100%;
  height: 260px;
  border: 0;
  display: block;
`;

export const Enter = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.7rem 1.1rem;
  border-radius: 8px;
  font-weight: 700;
  background: #5B9DD9;
  color: #0b0d12;
  &:disabled { opacity: 0.6; cursor: default; }
`;

// The organiser's door on their own card: shown when the signed-in person
// holds facilitate or better on this Kuutio (specs/access-model.md).
export const Facilitate = styled(Enter)`
  background: transparent;
  color: #5B9DD9;
  box-shadow: inset 0 0 0 1px #5B9DD9;
  font-weight: 600;
`;

export const Goal = styled.p`
  margin: 0.5rem 0 0;
  font-size: 1.05rem;
  line-height: 1.45;
  color: #c9d1dc;
  text-wrap: pretty;
`;

export const Countdown = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.5rem;
  color: #6fd3c8;
  font-weight: 600;
  font-size: 0.95rem;
`;

export const NoteItem = styled.li`
  list-style: none;
  padding-top: 0.7rem;
  border-top: 1px solid #232a36;

  &:first-of-type { padding-top: 0; border-top: 0; }

  strong { display: block; margin-bottom: 0.15rem; }
`;

export const NoteList = styled.ul`
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

export const Link = styled.a`
  color: #4cc7ce;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  text-underline-offset: 0.2em;

  &:focus-visible { outline: 2px solid #4cc7ce; outline-offset: 3px; }
`;

// Stays at the top while the page scrolls: the pre-work list is long on a phone
export const TabBar = styled.div`
  display: flex;
  gap: 0.25rem;
  border-bottom: 1px solid #232a36;
  position: sticky;
  top: 0;
  z-index: 5;
  background: #0b0d12;
  /* bleed to the page edges so nothing scrolls past beside it */
  margin: 0 -1.25rem;
  padding: 0 1.25rem;
`;

export const Tab = styled.button`
  all: unset;
  cursor: pointer;
  padding: 0.6rem 0.9rem;
  font-size: 0.95rem;
  color: ${p => (p.$active ? '#e8ecf2' : '#97a2b0')};
  border-bottom: 2px solid ${p => (p.$active ? '#4cc7ce' : 'transparent')};
  margin-bottom: -1px;

  &:hover { color: #e8ecf2; }
  &:focus-visible { outline: 2px solid #4cc7ce; outline-offset: -2px; }
`;

export const Back = styled.button`
  all: unset;
  cursor: pointer;
  color: #97a2b0;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  align-self: flex-start;

  &:focus-visible { outline: 2px solid #4cc7ce; outline-offset: 3px; }
`;
